import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SiteNav } from "@/components/marketing/SiteNav";
import { INTAKE_COPY, TOTAL_STEPS } from "@/components/marketing/BookingIntakeDialog";
import { supabase } from "@/integrations/supabase/client";
import { useSeoIndexing } from "@/hooks/useSeoIndexing";
import { calendlyInlineUrl } from "@/lib/marketingBooking";
import philPortrait from "@/assets/phil-portrait.webp";

type Lang = "en" | "de";

// Page-specific brand copy (kept local; the intake questions live in INTAKE_COPY).
const BRAND_COPY: Record<Lang, { eyebrow: string; trust: string }> = {
  en: {
    eyebrow: "Phil's Conversation",
    trust: "You're not taking a test. Phil reads your answers before the call, so your 15 minutes start exactly where you are.",
  },
  de: {
    eyebrow: "Phil's Conversation",
    trust: "Du machst keinen Test. Phil liest deine Antworten vor dem Gespräch, damit ihr nicht bei null anfangen müsst.",
  },
};

export default function ExpressionCheck({ lang }: { lang: Lang }) {
  const t = INTAKE_COPY[lang];
  const b = BRAND_COPY[lang];
  const [step, setStep] = useState(1);
  const [level, setLevel] = useState("");
  const [friction, setFriction] = useState<string[]>([]);
  const [frictionOther, setFrictionOther] = useState(false);
  const [frictionCustom, setFrictionCustom] = useState("");
  const [why, setWhy] = useState<string[]>([]);
  const [whyOther, setWhyOther] = useState(false);
  const [whyCustom, setWhyCustom] = useState("");
  const [phase, setPhase] = useState<"questions" | "calendar" | "booked">("questions");

  useSeoIndexing({ index: false, title: `${t.title} | Phil's Conversation`, description: t.intro, htmlLang: lang });

  // Calendly posts messages from the inline iframe; a scheduled event is the conversion.
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== "https://calendly.com") return;
      const ev = (e.data as { event?: string })?.event;
      if (ev === "calendly.event_scheduled") {
        (window as any).dataLayer?.push({ event: "invitee_meeting_scheduled" });
        setPhase("booked");
        window.scrollTo({ top: 0 });
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const pressable = "transition-transform duration-150 active:scale-[0.98] motion-reduce:transition-none";
  const toggle = (arr: string[], v: string) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const join = (arr: string[], other: boolean, custom: string) => [...arr, ...(other && custom.trim() ? [custom.trim()] : [])].join("; ");
  const effFriction = join(friction, frictionOther, frictionCustom);
  const effWhy = join(why, whyOther, whyCustom);
  const multiHint = lang === "de" ? "Mehrfachauswahl möglich" : "Choose all that apply";

  const optionCard = (label: string, selected: boolean, onSelect: () => void) => (
    <button
      key={label}
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`flex w-full items-center gap-3 rounded-xl border-2 px-4 py-3.5 text-left text-sm font-medium transition-colors ${pressable} ${
        selected ? "border-primary bg-primary/5 text-foreground" : "border-border bg-card text-foreground hover:border-primary/50"
      }`}
    >
      <span className="flex-1 leading-snug">{label}</span>
    </button>
  );

  const submit = () => {
    if (!effWhy) return;
    setPhase("calendar");
    window.scrollTo({ top: 0 });
    supabase.functions
      .invoke("submit-expression-check-intake", {
        body: { language: lang, level, friction: effFriction, why_matters: effWhy, source_page: window.location.pathname },
      })
      .catch((e) => console.error("intake save failed:", e));
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteNav lang={lang} current="home" />
      <main className="mx-auto w-full max-w-3xl px-4 py-10 sm:py-14">
        {phase === "booked" ? (
          <section className="mx-auto max-w-xl space-y-5 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
            <h1 className="text-3xl font-bold">
              {t.bookedTitle}
              <span className="mx-auto mt-2 block h-1 w-14 rounded-full bg-accent" aria-hidden />
            </h1>
            <p className="leading-relaxed text-muted-foreground">{t.bookedBody}</p>
            <Button asChild variant="outline"><Link to={lang === "de" ? "/de" : "/"}>{t.backHome}</Link></Button>
          </section>
        ) : phase === "calendar" ? (
          <section className="space-y-4">
            <div className="space-y-3">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-primary">
                <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden />
                {b.eyebrow}
              </p>
              <h1 className="text-3xl font-bold">
                {t.calTitle}
                <span className="mt-2 block h-1 w-14 rounded-full bg-accent" aria-hidden />
              </h1>
            </div>
            <p className="leading-relaxed text-muted-foreground">{t.doneBody}</p>
            <div className="overflow-hidden rounded-2xl border border-border bg-card">
              <iframe
                title={t.calTitle}
                src={calendlyInlineUrl(lang, window.location.hostname)}
                className="block h-[1150px] w-full"
                loading="eager"
              />
            </div>
            <Button variant="ghost" onClick={() => { setPhase("questions"); setStep(1); }} className={pressable}>
              <ArrowLeft className="mr-1.5 h-4 w-4" />{t.edit}
            </Button>
          </section>
        ) : (
          <section className="mx-auto max-w-xl space-y-5">
            <div className="space-y-3">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-primary">
                <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden />
                {b.eyebrow}
              </p>
              <div className="space-y-2">
                <h1 className="text-3xl font-bold">
                  {t.title}
                  <span className="mt-2 block h-1 w-14 rounded-full bg-accent" aria-hidden />
                </h1>
                <p className="leading-relaxed text-muted-foreground">{t.intro}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-3.5">
              <img
                src={philPortrait}
                alt="Philip Schiller, English Communication Coach"
                width={44}
                height={44}
                className="h-11 w-11 shrink-0 rounded-full object-cover object-top ring-2 ring-accent/40"
                loading="lazy"
                decoding="async"
              />
              <p className="text-sm leading-snug text-muted-foreground">{b.trust}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex gap-1.5" aria-hidden>
                {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                  <span
                    key={i}
                    className={`h-1.5 rounded-full transition-all duration-200 motion-reduce:transition-none ${
                      i + 1 === step ? "w-6 bg-accent" : i + 1 < step ? "w-3 bg-primary" : "w-3 bg-border"
                    }`}
                  />
                ))}
              </span>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">{t.step(step, TOTAL_STEPS)}</p>
            </div>

            {step === 1 && (
              <fieldset className="space-y-2.5">
                <legend className="mb-3 text-base font-bold leading-snug">{t.levelQ}</legend>
                {t.levels.map((l) => optionCard(l, level === l, () => { setLevel(l); setStep(2); }))}
              </fieldset>
            )}
            {step === 2 && (
              <fieldset className="space-y-2.5">
                <legend className="mb-3 text-base font-bold leading-snug">{t.frictionQ}<span className="mt-1 block text-xs font-medium text-muted-foreground">{multiHint}</span></legend>
                {t.frictions.map((f) => optionCard(f, friction.includes(f), () => setFriction((a) => toggle(a, f))))}
                {optionCard(t.frictionOther, frictionOther, () => setFrictionOther((v) => !v))}
                {frictionOther && <Textarea value={frictionCustom} onChange={(e) => setFrictionCustom(e.target.value)} placeholder={t.frictionOtherPh} rows={3} autoFocus className="text-sm" />}
              </fieldset>
            )}
            {step === 3 && (
              <fieldset className="space-y-2.5">
                <legend className="mb-3 text-base font-bold leading-snug">{t.whyQ}<span className="mt-1 block text-xs font-medium text-muted-foreground">{multiHint}</span></legend>
                {t.whys.map((w) => optionCard(w, why.includes(w), () => setWhy((a) => toggle(a, w))))}
                {optionCard(t.whyOther, whyOther, () => setWhyOther((v) => !v))}
                {whyOther && <Textarea value={whyCustom} onChange={(e) => setWhyCustom(e.target.value)} placeholder={t.whyOtherPh} rows={3} autoFocus className="text-sm" />}
              </fieldset>
            )}

            <div className="flex items-center justify-between gap-3 pt-1">
              {step > 1 ? (
                <Button type="button" variant="ghost" onClick={() => setStep((s) => s - 1)} className={pressable}>
                  <ArrowLeft className="mr-1.5 h-4 w-4" />{t.back}
                </Button>
              ) : <span />}
              {step < TOTAL_STEPS ? (
                <Button type="button" onClick={() => setStep((s) => s + 1)} disabled={(step === 1 && !level) || (step === 2 && !effFriction)} className={pressable}>
                  {t.next}<ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              ) : (
                <Button type="button" onClick={submit} disabled={!effWhy} size="lg" className={`h-12 text-base font-bold ${pressable}`}>
                  {t.submit}
                </Button>
              )}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
